import { Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { isUndefined } from 'lodash';
import { useEffect } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { Card } from '@/components/layout/Card/Card';
import { useFormContext } from '@/components/react-hook-form';
import {
  DispositiveProvisionDispositionKind,
  DispositiveProvisionTemplateKind,
  DispositiveProvisionTransferTaxKind,
} from '@/types/schema';

import { DISPOSITIVE_PROVISIONS_FORM_NAMESPACE } from '../../DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import {
  DispositiveProvisionsFormPaths,
  DispositiveProvisionsFormRecipient,
  DispositiveProvisionsTemplateSlotKind,
} from '../../DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { DispositiveProvisionsTemplateEditorTabHeader } from '../DispositiveProvisionsTemplateSplitScreenModal.components';
import {
  DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE,
  DispositiveProvisionsTemplateShape,
} from '../DispositiveProvisionsTemplateSplitScreenModal.types';
import {
  BLANK_BYPASS_TRUST,
  TemplateRecipientSlot,
} from './DispositiveProvisionsTemplateSplitScreenModal.components';

export function DispositiveProvisionsTemplateABTrustPreset() {
  const { control, setValue } =
    useFormContext<DispositiveProvisionsTemplateShape>();
  const {
    append,
    replace,
    fields: recipientFields,
  } = useFieldArray({
    control,
    name: `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients` as const satisfies DispositiveProvisionsFormPaths,
    keyName: '_id',
  });
  const [includeSurvivorsTrust, recipients] = useWatch({
    control,
    name: [
      `${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.includeSurvivorsTrust`,
      `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients`,
    ],
  });
  const includeSurvivorsTrustPrevious = usePrevious(includeSurvivorsTrust);

  useEffect(() => {
    if (
      isUndefined(includeSurvivorsTrustPrevious) ||
      includeSurvivorsTrustPrevious === includeSurvivorsTrust
    )
      return;

    const hasSurvivor = recipients.some(
      (recipient) =>
        recipient._templateSlotKind ===
        DispositiveProvisionsTemplateSlotKind.AbSurvivor
    );
    if (includeSurvivorsTrust === hasSurvivor) return;

    replace(
      includeSurvivorsTrust
        ? [BLANK_SURVIVOR_TRUST, ...recipients]
        : recipients.filter(
            (recipient) =>
              recipient._templateSlotKind !==
              DispositiveProvisionsTemplateSlotKind.AbSurvivor
          )
    );
  }, [
    includeSurvivorsTrust,
    includeSurvivorsTrustPrevious,
    recipients,
    replace,
  ]);

  const handleRemoveRecipient = (index: number) => {
    const recipient = recipients[index];
    const nextRecipients = recipients.filter(
      (_, recipientIndex) => recipientIndex !== index
    );
    replace(nextRecipients);
    if (
      recipient?._templateSlotKind ===
      DispositiveProvisionsTemplateSlotKind.AbSurvivor
    ) {
      setValue(
        `${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.includeSurvivorsTrust`,
        false
      );
    }
  };

  return (
    <Stack direction="column" spacing={2}>
      <DispositiveProvisionsTemplateEditorTabHeader
        templateType={DispositiveProvisionTemplateKind.AbTrust}
        recipients={recipientFields}
        onAddTemplateRecipient={append}
      />
      <Card variant="filled-callout" sx={{ padding: 3 }}>
        <FormAwareSwitch<DispositiveProvisionsTemplateShape>
          control={control}
          label="Include survivor's trust"
          fieldName={`${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.includeSurvivorsTrust`}
          labelPosition="right"
        />
      </Card>
      {recipientFields.map((recipient, index) => (
        <TemplateRecipientSlot
          key={recipient._id}
          index={index}
          slotKind={recipient._templateSlotKind}
          onRemove={() => handleRemoveRecipient(index)}
        />
      ))}
    </Stack>
  );
}

const BLANK_SURVIVOR_TRUST: DispositiveProvisionsFormRecipient = {
  _templateSlotKind: DispositiveProvisionsTemplateSlotKind.AbSurvivor,
  _dispositiveProvisionId: null,
  assetGroupIds: [],
  assetStaticIds: [],
  insurancePolicyIds: [],
  ownershipStakeIds: [],
  dispositionAssetPercentage: null,
  recipientId: '',
  recipientKind: null,
  dispositionKind_ButtonGroup: DispositiveProvisionDispositionKind.Percentage,
  dispositionKind_Select: '',
  dispositionAmount: null,
  dispositionPercentage: new Decimal(50),
  notes: '',
  transferTaxKind: DispositiveProvisionTransferTaxKind.SpouseMaritalExclusion,
};

export const BLANK_AB_GST_EXEMPT_MARITAL_TRUST: DispositiveProvisionsFormRecipient =
  {
    _templateSlotKind: DispositiveProvisionsTemplateSlotKind.AbGstExempt,
    _dispositiveProvisionId: null,
    assetGroupIds: [],
    assetStaticIds: [],
    insurancePolicyIds: [],
    ownershipStakeIds: [],
    dispositionAssetPercentage: null,
    recipientId: '',
    recipientKind: null,
    dispositionKind_ButtonGroup: 'other',
    dispositionKind_Select:
      DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantorInExcessOfLifetimeExclusion,
    dispositionAmount: null,
    dispositionPercentage: new Decimal(100),
    notes: '',
    transferTaxKind: DispositiveProvisionTransferTaxKind.SpouseMaritalExclusion,
  };

export const BLANK_AB_GST_NON_EXEMPT_MARITAL_TRUST: DispositiveProvisionsFormRecipient =
  {
    _templateSlotKind: DispositiveProvisionsTemplateSlotKind.AbGstNonExempt,
    _dispositiveProvisionId: null,
    assetGroupIds: [],
    assetStaticIds: [],
    insurancePolicyIds: [],
    ownershipStakeIds: [],
    dispositionAssetPercentage: null,
    recipientId: '',
    recipientKind: null,
    dispositionKind_ButtonGroup: 'other',
    dispositionKind_Select:
      DispositiveProvisionDispositionKind.AnythingLeftOver,
    dispositionAmount: null,
    dispositionPercentage: new Decimal(100),
    notes: '',
    transferTaxKind: DispositiveProvisionTransferTaxKind.SpouseMaritalExclusion,
  };
export const DEFAULT_AB_TRUST_RECIPIENTS: DispositiveProvisionsFormRecipient[] =
  [
    BLANK_BYPASS_TRUST,
    BLANK_AB_GST_EXEMPT_MARITAL_TRUST,
    BLANK_AB_GST_NON_EXEMPT_MARITAL_TRUST,
  ];
